'use client';

import React, { useState } from 'react';
import {
    SiReact, SiNextdotjs, SiTypescript, SiMongodb, SiVite, SiNodedotjs,
    SiIos, SiVuedotjs, SiFlutter, SiIonic, SiFlask, SiDjango, SiLaravel, SiMysql,
    SiPython, SiJavascript, SiCplusplus, SiC, SiGit, SiDocker,
    SiPytorch, SiTensorflow, SiPandas, SiNumpy, SiScikitlearn, SiOpencv,
    SiArduino, SiRaspberrypi, SiExpress, SiStripe, SiTurborepo, SiThreedotjs,
    SiServerless, SiBlockchaindotcom, SiAssemblyscript, SiHuggingface, SiArm, SiRiscv,
    SiHtml5, SiPhp, SiRuby, SiShell, SiKotlin, SiRust, SiGo, SiClojure,
    SiCss, SiExpo, SiBluetooth, SiSwift, SiAndroid, SiSmartthings, SiPlatformio,
    SiTailwindcss, SiRedis, SiRedux, SiEslint, SiGooglesearchconsole, SiGooglegemini, SiAngular, SiDarkreader
} from 'react-icons/si';
import {
    FaMicrochip, FaCogs, FaRobot,
    FaLaptopCode,
    FaJava
} from 'react-icons/fa';
import {
GiArtificialIntelligence
} from 'react-icons/gi';
import {
    BsFiletypeScss
} from 'react-icons/bs';
import { CodeBracketIcon } from '@heroicons/react/24/outline';

interface TechIconProps {
    tech: string;
    className?: string;
}

const TechIcon = ({ tech, className = '' }: TechIconProps) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const isSkillsIcon = className?.includes('skills-icon');
    const iconClass = isSkillsIcon ? 'w-8 h-8' : 'w-5 h-5';

    const getTechIcon = (technology: string) => {
        const tech = technology.toLowerCase().trim();

        // Frontend Frameworks & Libraries
        if (tech.includes('react native')) return <SiReact className={iconClass} />;
        if (tech.includes('expo')) return <SiExpo className={iconClass} />;
        if (tech.includes('react')) return <SiReact className={iconClass} />;
        if (tech.includes('next') || tech.includes('nextjs')) return <SiNextdotjs className={iconClass} />;
        if (tech.includes('vue')) return <SiVuedotjs className={iconClass} />;
        if (tech.includes('three') || tech.includes('threejs')) return <SiThreedotjs className={iconClass} />;
        if (tech === 'ai') return <GiArtificialIntelligence className={iconClass} />;

        // Programming Languages
        if (tech.includes('typescript')) return <SiTypescript className={iconClass} />;
        if (tech.includes('javascript')) return <SiJavascript className={iconClass} />;
        if (tech.includes('python')) return <SiPython className={iconClass} />;
        if (tech.includes('c++')) return <SiCplusplus className={iconClass} />;
        if (tech.includes('html')) return <SiHtml5 className={iconClass} />;
        if (tech.includes('php')) return <SiPhp className={iconClass} />;
        if (tech.includes('ruby')) return <SiRuby className={iconClass} />;
        if (tech.includes('shell')) return <SiShell className={iconClass} />;
        if (tech.includes('kotlin')) return <SiKotlin className={iconClass} />;
        if (tech.includes('rust')) return <SiRust className={iconClass} />;
        if (tech.includes('clojure')) return <SiClojure className={iconClass} />;
        if (tech === 'go' || tech === 'golang') return <SiGo className={iconClass} />;
        if (tech.includes('c++')) return <SiCplusplus className={iconClass} />;
        if (tech.toLowerCase() === 'c') return <SiC className={iconClass} />;
        if (tech.toLowerCase() === 'scss') return <BsFiletypeScss className={iconClass} />;
        if (tech.toLowerCase() === 'css') return <SiCss className={iconClass} />;

        // Backend Frameworks
        if (tech.includes('node') || tech.includes('nodejs')) return <SiNodedotjs className={iconClass} />;
        if (tech.includes('express')) return <SiExpress className={iconClass} />;
        if (tech.includes('flask')) return <SiFlask className={iconClass} />;
        if (tech.includes('django')) return <SiDjango className={iconClass} />;
        if (tech.includes('laravel')) return <SiLaravel className={iconClass} />;

        // Mobile Development
        if (tech.includes('flutter')) return <SiFlutter className={iconClass} />;
        if (tech.includes('ionic')) return <SiIonic className={iconClass} />;
        if (tech.includes('swiftui')) return <SiSwift className={iconClass} />;
        if (tech.includes('swift')) return <SiSwift className={iconClass} />;
        if (tech.includes('ios')) return <SiIos className={iconClass} />;
        if (tech.includes('android')) return <SiAndroid className={iconClass} />;

        // IoT / Hardware
        if (tech.includes('ble') || tech.includes('bluetooth')) return <SiBluetooth className={iconClass} />;
        if (tech.includes('iot') || tech.includes('internet of things')) return <SiSmartthings className={iconClass} />;
        if (tech.includes('cross platform') || tech.includes('cross-platform') || tech.includes('multiplatform') || tech.includes('multi platform')) return <SiPlatformio className={iconClass} />;

        // Databases
        if (tech.includes('mongodb') || tech.includes('mongo')) return <SiMongodb className={iconClass} />;
        if (tech.includes('mysql') || tech.includes('sql')) return <SiMysql className={iconClass} />;
        if (tech.includes('redis')) return <SiRedis className={iconClass} />;

        // Developer tools / product features
        if (tech.includes('tailwind')) return <SiTailwindcss className={iconClass} />;
        if (tech.includes('eslint')) return <SiEslint className={iconClass} />;
        if (tech.includes('app router') || tech.includes('app-router') || (tech.includes('app') && tech.includes('router'))) return <SiNextdotjs className={iconClass} />;
        if (tech.includes('seo')) return <SiGooglesearchconsole className={iconClass} />;
        if (tech.includes('dark mode') || tech.includes('darkmode') || tech.includes('dark')) return <SiDarkreader className={iconClass} />;
        if (tech.includes('redux')) return <SiRedux className={iconClass} />;
        if (tech.includes('angular')) return <SiAngular className={iconClass} />;
        if (tech.includes('gemini')) return <SiGooglegemini className={iconClass} />;
        if (tech.includes('mysql') || tech.includes('sql')) return <SiMysql className={iconClass} />;

        // AI/ML & Data Science
        if (tech.includes('pytorch')) return <SiPytorch className={iconClass} />;
        if (tech.includes('tensorflow')) return <SiTensorflow className={iconClass} />;
        if (tech.includes('pandas')) return <SiPandas className={iconClass} />;
        if (tech.includes('numpy')) return <SiNumpy className={iconClass} />;
        if (tech.includes('scikit') || tech.includes('sklearn')) return <SiScikitlearn className={iconClass} />;
        if (tech.includes('opencv')) return <SiOpencv className={iconClass} />;

        // Tools & Platforms
        if (tech.includes('git')) return <SiGit className={iconClass} />;
        if (tech.includes('docker')) return <SiDocker className={iconClass} />;
        if (tech.includes('vite')) return <SiVite className={iconClass} />;
        if (tech.includes('turbo') || tech.includes('turborepo')) return <SiTurborepo className={iconClass} />;
        if (tech.includes('serverless')) return <SiServerless className={iconClass} />;

        // Hardware & IoT
        if (tech.includes('arduino')) return <SiArduino className={iconClass} />;
        if (tech.includes('raspberry')) return <SiRaspberrypi className={iconClass} />;

        // Web3 & Blockchain
        if (tech.includes('blockchain') || tech.includes('web3')) return <SiBlockchaindotcom className={iconClass} />;

        // Payment
        if (tech.includes('stripe')) return <SiStripe className={iconClass} />;

        // Generic fallback icons for categories
        if (tech.includes('hugging face')) return <SiHuggingface className={iconClass} />;
        if (tech.includes('nltk')) return <SiPython className={iconClass} />;
        if (tech.includes('esp32') || tech.includes('esp8266')) return <FaMicrochip className={iconClass} />;
        if (tech.includes('risc-v') || tech.includes('avr')) return <SiRiscv className={iconClass} />;
        if (tech.includes('arm')) return <SiArm className={iconClass} />;
        if (tech.includes('assembly') || tech.includes('f#')) return <SiAssemblyscript className={iconClass} />;
        if (tech.includes('ci/cd')) return <FaCogs className={iconClass} />;
        if (tech.includes('quantum computing')) return <FaRobot className={iconClass} />;
        if (tech.includes('system design')) return <FaLaptopCode className={iconClass} />;
        if (tech.includes('cra')) return <SiReact className={iconClass} />;
        if (tech.includes('java') && !tech.includes('javascript')) return <FaJava className={iconClass} />;

        // Default fallback
        return <CodeBracketIcon className={iconClass} />;
    };

    const getTechColor = (technology: string) => {
        const tech = technology.toLowerCase();

        if (tech.includes('react')) return 'text-blue-400';
        if (tech.includes('next')) return 'text-white';
        if (tech.includes('javascript')) return 'text-yellow-400';

        if (tech.includes('typescript')) return 'text-blue-600';
        if (tech.includes('mongodb')) return 'text-green-500';
        if (tech.includes('vite')) return 'text-purple-500';
        if (tech.includes('node')) return 'text-green-500';
        if (tech.includes('vue')) return 'text-green-400';
        if (tech.includes('flutter')) return 'text-blue-500';
        if (tech.includes('ionic')) return 'text-blue-400';
        if (tech.includes('flask')) return 'text-gray-300';
        if (tech.includes('django')) return 'text-green-600';
        if (tech.includes('laravel')) return 'text-red-500';
        if (tech.includes('sql')) return 'text-orange-500';
        if (tech.includes('php')) return 'text-blue-500';
        if (tech.includes('ruby')) return 'text-red-500';
        if (tech.includes('shell')) return 'text-green-500';
        if (tech.includes('kotlin')) return 'text-purple-500';
        if (tech.includes('swift')) return 'text-pink-400';
        if (tech.includes('android')) return 'text-green-500';
        if (tech.includes('ble') || tech.includes('bluetooth')) return 'text-sky-500';
        if (tech.includes('iot') || tech.includes('internet of things')) return 'text-cyan-400';
        if (tech.includes('cross platform') || tech.includes('cross-platform') || tech.includes('multiplatform') || tech.includes('multi platform')) return 'text-sky-400';
        if (tech.includes('redis')) return 'text-red-500';
        if (tech.includes('redux')) return 'text-violet-500';
        if (tech.includes('tailwind')) return 'text-sky-400';
        if (tech.includes('eslint')) return 'text-yellow-400';
        if (tech.includes('seo')) return 'text-orange-400';
        if (tech.includes('dark mode') || tech.includes('darkmode') || tech.includes('dark')) return 'text-slate-300';
        if (tech.includes('angular')) return 'text-red-600';
        if (tech.includes('gemini')) return 'text-indigo-400';
        if (tech.includes('rust')) return 'text-orange-500';
        if (tech.includes('go')) return 'text-blue-400';
        if (tech.includes('clojure')) return 'text-green-400';
        if (tech.includes('css')) return 'text-blue-500';
        if (tech.includes('scss')) return 'text-pink-500';
        if (tech.includes('python')) return 'text-yellow-400';
        if (tech.includes('git')) return 'text-orange-600';
        if (tech.includes('docker')) return 'text-blue-500';
        if (tech.includes('c++')) return 'text-blue-500';
        if (tech.includes('c') && !tech.includes('++')) return 'text-blue-600';
        if (tech.includes('threejs') || tech.includes('three')) return 'text-orange-400';
        if (tech.includes('stripe')) return 'text-purple-500';
        if (tech.includes('turbo') || tech.includes('tubro')) return 'text-red-400';
        if (tech.includes('cra')) return 'text-blue-400';
        if (tech.includes('express')) return 'text-gray-300';
        if (tech.includes('serverless')) return 'text-orange-400';
        if (tech.includes('nodejs')) return 'text-green-500';
        if (tech.includes('pytorch')) return 'text-orange-500';
        if (tech.includes('tensorflow')) return 'text-orange-400';
        if (tech.includes('pandas')) return 'text-blue-500';
        if (tech.includes('numpy')) return 'text-blue-400';
        if (tech.includes('scikit') || tech.includes('sklearn')) return 'text-orange-300';
        if (tech.includes('opencv')) return 'text-green-400';
        if (tech.includes('arduino')) return 'text-teal-400';
        if (tech.includes('raspberry')) return 'text-red-400';
        if (tech.includes('assembly')) return 'text-gray-500';
        if (tech.includes('f#')) return 'text-blue-600';
        if (tech.includes('ci/cd')) return 'text-green-500';
        if (tech.includes('web3')) return 'text-purple-400';
        if (tech.includes('blockchain')) return 'text-yellow-500';

        return 'text-gray-400';
    };

    const iconSize = isSkillsIcon ? 'w-auto h-12' : 'w-10 h-7';
    const rawIcon = getTechIcon(tech);
    const accessibleIcon = React.isValidElement(rawIcon)
        ? React.cloneElement(rawIcon as React.ReactElement<{ 'aria-hidden'?: boolean; role?: string }>, {
            'aria-hidden': true,
            role: 'presentation',
        })
        : rawIcon;

    return (
        <div
            title={tech}
            className={`relative inline-flex items-center ${isSkillsIcon ? 'gap-3 px-4 py-2' : 'justify-center'} ${iconSize} ${getTechColor(tech)} bg-zinc-800 hover:bg-zinc-700 rounded-lg text-xs font-medium transition-all duration-200 hover:scale-105 cursor-pointer ${className}`}
            onMouseEnter={() => !isSkillsIcon && setShowTooltip(true)}
            onMouseLeave={() => !isSkillsIcon && setShowTooltip(false)}
        >
            <div className="flex items-center justify-center">
                {accessibleIcon}
            </div>
            {isSkillsIcon && (
                <span className="text-white font-medium">{tech}</span>
            )}

            {/* Tooltip for small icons only */}
            {!isSkillsIcon && showTooltip && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-[9999] pointer-events-none shadow-lg border border-gray-700">
                    {tech}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
            )}
        </div>
    );
};

export default TechIcon;